import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import cx from "classnames";
import { useTranslation } from "react-i18next";

import { Copy } from "@/components/icons/copy";
import { DragVertical } from "@/components/icons/drag-vertical";
import { Edit } from "@/components/icons/edit";
import { Trash } from "@/components/icons/trash";
import { Tooltip } from "@/components/shared/tooltip";
import type { VideoLibraryAnalyticsProps } from "@/content-hub/analytics/video-library-events";
import { trackLibraryVideoCopyClick } from "@/content-hub/analytics/video-library-events";
import { copyVideoSharePageUrlAndShowToast } from "@/content-hub/utils/video-share-copied-toast";

type SortableVideoItemProps = {
  video: {
    id: string;
    title?: string | null;
    thumbnail?: string;
    aspectRatio?: string;
    kind?: "recording" | "embed";
    listIndex?: number;
  };
  deleteVideo: () => void;
  /** When set (e.g. `contentHubDirectVideoSharing` on), row shows copy share link control. */
  getShareUrl?: () => string;
  /** When set (recording or embed rows + direct sharing), opens metadata slide-over. */
  onEditClick?: () => void;
  analytics: VideoLibraryAnalyticsProps;
};

export function SortableVideoItem(props: SortableVideoItemProps) {
  const { t } = useTranslation();
  const { video, deleteVideo, getShareUrl, onEditClick, analytics } = props;
  const isPortrait = video?.aspectRatio === "9_16";

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: video.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleCopyShare = () => {
    if (!getShareUrl) return;
    const url = getShareUrl();
    if (!url) return;
    trackLibraryVideoCopyClick(analytics);
    void copyVideoSharePageUrlAndShowToast(url);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cx(
        "mb-2 flex h-10 w-full items-center gap-x-2 rounded-lg border border-solid border-black/20 p-2",
        { "z-10": isDragging }
      )}
    >
      <div {...attributes} {...listeners}>
        <DragVertical
          className={cx("size-4 outline-none", {
            "cursor-grabbing": isDragging,
            "cursor-grab": !isDragging,
          })}
        />
      </div>
      <div className="flex h-[30px] w-[calc(16/9*30px)] shrink-0 items-center justify-center overflow-hidden rounded-[4px]">
        <img
          src={video.thumbnail}
          alt={video.title ?? ""}
          draggable={false}
          className={cx("rounded-[4px] object-contain object-center", {
            "h-[30px] w-[calc(16/9*30px)]": !isPortrait,
            "max-h-full max-w-full shadow-sm ring-1 ring-black/10": isPortrait,
          })}
        />
      </div>
      <div
        className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800"
        title={video.title ?? undefined}
      >
        {video.title}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {getShareUrl ? (
          <Tooltip
            placement="top-center"
            tooltip={t("preference-panel.copy-video-share-link")}
            shouldShow
          >
            <button
              type="button"
              className="flex rounded p-0.5 text-gray-700 outline-none hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label={t("preference-panel.copy-video-share-link")}
              onClick={handleCopyShare}
            >
              <Copy className="size-4" aria-hidden />
            </button>
          </Tooltip>
        ) : null}
        {onEditClick ? (
          <Tooltip placement="top-center" tooltip={t("common.edit")} shouldShow>
            <button
              type="button"
              className="flex rounded p-0.5 text-gray-700 outline-none hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label={t("common.edit")}
              onClick={onEditClick}
            >
              <Edit className="size-4" aria-hidden />
            </button>
          </Tooltip>
        ) : null}
        <Tooltip placement="top-center" tooltip={t("common.delete")} shouldShow>
          <button
            type="button"
            className="flex rounded p-0.5 text-gray-700 outline-none hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            aria-label={t("common.delete")}
            onClick={deleteVideo}
          >
            <Trash className="size-4" aria-hidden />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
